'use client'

import { type IPaginationMethods, type IParamsRef } from '@repo/sdk'
import { useImperativeHandle, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

export type IConcatParams = IFilterRecord | IPaginationRecord | ISortRecord
export type IFilterRecord = Record<string, number | string | undefined>
export type IPaginationRecord = Record<string, number | string | undefined>
export interface IParamsObject {
  filter?: IFilterRecord
  pagination?: IPaginationRecord
  search?: ISearchQuery
  sort?: ISortRecord
  status?: IStatusType
}
export type ISearchQuery = string | undefined
export type ISortRecord = Record<string, number | string | undefined>

export type IStatusType = string | undefined

interface IDefaultParams {
  filter?: IFilterRecord
  pagination?: IPaginationRecord
  sort?: ISortRecord
  status?: string
}

const DEFAULT_PER_PAGE = 10

export const DEFAULT_PAGINATION_DATA = { page: 1, perPage: DEFAULT_PER_PAGE }

export const useParams = (defaultParams: IDefaultParams = {}, refreshNextPage = true) => {
  const paginationRef = useRef<IPaginationMethods>(undefined)
  const paramsRef = useRef<IParamsRef>(undefined)
  const [paramsPages, setParamsPages] = useState<string[]>([])

  useImperativeHandle(paramsRef, () => ({
    getOrSaveAndGetParams(idx: number) {
      if (paramsPages.length < idx) {
        setParamsPages([...paramsPages, paramsString])
        return { paramsString }
      }

      return { isReFetch: true, paramsString: paramsPages[idx - 1] }
    },
  }))

  const [params, setParams] = useState<IParamsObject>({
    filter: defaultParams.filter || {},
    pagination: defaultParams.pagination || DEFAULT_PAGINATION_DATA,
    search: undefined,
    sort: defaultParams.sort || {},
    status: defaultParams.status,
  })

  const concatParams = (data: IConcatParams) =>
    Object.values(data)
      .filter(data => data)
      .join('&')

  const concatParamsAndKey = (data?: IConcatParams, title?: string) => {
    const parsedData =
      data &&
      Object.keys(data).reduce(
        (acc, cur) => (cur && data[cur] ? { ...acc, [cur]: data[cur] } : acc),
        {},
      )

    return (
      parsedData &&
      Object.values(parsedData)
        .map(
          (value, i) =>
            `${title || Object.keys(parsedData)[i]}${title ? `[${Object.keys(parsedData)[i]}]` : ''}=${typeof value === 'string' || typeof value === 'number' ? value : ''}`,
        )
        .join('&')
    )
  }

  const paramsComponents = {
    filter: concatParamsAndKey(params.filter, 'filter'),
    pagination: concatParamsAndKey(params.pagination),
    search: params.search && `search=${params.search}`,
    sort: concatParamsAndKey(params.sort, 'sort'),
    status: params.status,
  }

  const paramsString = useMemo(
    () => concatParams(paramsComponents),
    [
      refreshNextPage ? params.pagination : 0,
      params.pagination?.per_page,
      params.filter,
      params.status,
      params.sort,
      params.search,
    ],
  )

  const newPage = params.pagination?.current_page || params.pagination?.page
  return {
    page: newPage ? (typeof newPage === 'number' ? newPage : z.coerce.number().parse(newPage)) : 1,
    paginationRef,
    params: paramsString,
    paramsComponents,
    paramsObject: params,
    paramsRef,
    setFilter(data: IFilterRecord) {
      setParams({
        ...params,
        filter: data,
        pagination: paginationRef.current?.getDataResetPage(),
      })
    },
    setPagination(data: IPaginationRecord) {
      setParams({
        ...params,
        pagination: {
          ...data,
          page: data.current_page || data.page,
        },
      })
    },
    setSearch(search: ISearchQuery) {
      setParams({
        ...params,
        search,
      })
    },
    setSort(data: ISortRecord) {
      setParams({
        ...params,
        pagination: paginationRef.current?.getDataResetPage(),
        sort: data,
      })
    },
    setStatus(status: IStatusType) {
      setParams({
        filter: params.filter,
        pagination: params.pagination,
        sort: params.sort,
        status,
      })
    },
    sort: params.sort,
  }
}
